// src/components/BlogList.js
import { useEffect, useState } from "react";
import axios from "axios";
import { messaging } from "../firebase";
import { getToken, onMessage } from "firebase/messaging";

export default function BlogList({ userId }) {
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [nativeAd, setNativeAd] = useState(null); // New state for native ad

  useEffect(() => {
    // Fetch latest blogs
    fetchBlogs();

    // Register device for push notifications
    const registerToken = async () => {
      try {
        const token = await getToken(messaging, {
          vapidKey: "YOUR_VAPID_KEY", // replace with Firebase VAPID key
        });

        if (token) {
          await axios.post("https://your-api.com/api/device-tokens", {
            token,
            userId,
          });
          console.log("✅ Device token registered:", token);
        }
      } catch (err) {
        console.error("❌ Failed to register device token:", err);
      }
    };

    registerToken();

    // Listen to incoming push notifications
    const unsubscribe = onMessage(messaging, (payload) => {
      console.log("Push notification received: ", payload);
      const newNotification = {
        title: payload.notification.title,
        body: payload.notification.body,
      };
      setNotifications((prev) => [newNotification, ...prev]);
      alert(`📢 ${payload.notification.title}\n${payload.notification.body}`);
    });

    // Setup native ad listener for WebView
    window.onNativeAdLoaded = (adData) => {
      console.log("Native ad received:", adData);
      setNativeAd(adData);
    };

    // Request native ad from Android after a short delay
    setTimeout(() => {
      if (window.AndroidNative && window.AndroidNative.showNativeAd) {
        window.AndroidNative.showNativeAd();
      } else {
        console.warn("AndroidNative interface not available");
      }
    }, 1000); // delay to ensure WebView is ready

    return unsubscribe;
  }, []);

  const fetchBlogs = async () => {
    try {
      const res = await fetch(
        "https://globisun.com/wp-json/wp/v2/posts?_embed&per_page=10"
      );
      const data = await res.json();
      setPosts(data);
    } catch (err) {
      console.error("Blog fetch failed:", err);
      setPosts([]);
    }
  };

  return (
    <div className="blog-section">
      <h2 className="blog-section-title">📚 Latest Blogs</h2>

      {/* Optional In-app Notifications */}
      {notifications.length > 0 && (
        <div className="in-app-notifications">
          {notifications.map((n, i) => (
            <div key={i} className="notification-card">
              <strong>{n.title}</strong>: {n.body}
            </div>
          ))}
        </div>
      )}

      <div className="blog-scroll" role="list">
        {posts.map((post) => {
          const featuredImage =
            post._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "";
          const date = new Date(post.date).toLocaleDateString();

          return (
            <article
              key={post.id}
              role="listitem"
              className="blog-card"
              onClick={() => setSelectedPost(post)}
            >
              <img
                src={featuredImage}
                alt={post.title.rendered || "blog image"}
                className="blog-image"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
              <div className="blog-meta">
                <div className="blog-date">{date}</div>
                <h3
                  className="blog-title"
                  dangerouslySetInnerHTML={{ __html: post.title.rendered }}
                />
                <div
                  className="blog-excerpt"
                  dangerouslySetInnerHTML={{ __html: post.excerpt.rendered }}
                />
                <div className="blog-readmore">Read more →</div>
              </div>
            </article>
          );
        })}
      </div>

      {/* Native Ad Section */}
      {nativeAd && (
        <div className="native-ad-section">
          {nativeAd.imageUrl && !nativeAd.hasVideo && (
            <img
              src={nativeAd.imageUrl}
              alt={nativeAd.headline || "Ad image"}
              className="native-ad-image"
            />
          )}

          {/* Video if present */}
          {nativeAd.hasVideo && nativeAd.mediaContentUrl && (
            <video
              src={nativeAd.mediaContentUrl}
              controls
              className="native-ad-video"
            />
          )}

          <h3 className="native-ad-title">{nativeAd.headline}</h3>
          {nativeAd.body && <p className="native-ad-body">{nativeAd.body}</p>}
          {nativeAd.iconUrl && (
            <img
              src={nativeAd.iconUrl}
              alt="Ad icon"
              className="native-ad-icon"
            />
          )}
          {nativeAd.callToAction && (
            <button className="native-ad-cta">{nativeAd.callToAction}</button>
          )}
        </div>
      )}

      {/* Blog Modal */}
      {selectedPost && (
        <div
          className="blog-modal-overlay"
          onClick={(e) => {
            if (e.target.classList.contains("blog-modal-overlay"))
              setSelectedPost(null);
          }}
        >
          <div className="blog-modal" role="dialog" aria-modal="true">
            <button
              className="blog-modal-close"
              aria-label="close"
              onClick={() => setSelectedPost(null)}
            >
              ✖
            </button>

            {selectedPost._embedded?.["wp:featuredmedia"]?.[0]?.source_url && (
              <img
                src={selectedPost._embedded["wp:featuredmedia"][0].source_url}
                alt={selectedPost.title.rendered}
                className="blog-modal-image"
              />
            )}

            <div className="blog-modal-date">
              {new Date(selectedPost.date).toLocaleDateString()}
            </div>

            <h2
              className="blog-modal-title"
              dangerouslySetInnerHTML={{ __html: selectedPost.title.rendered }}
            />

            <div
              className="blog-modal-content"
              dangerouslySetInnerHTML={{ __html: selectedPost.content.rendered }}
            />

            {/* See More Section */}
            <div className="see-more-section">
              <h3 className="see-more-title">See More Posts</h3>
              <div className="see-more-scroll">
                {posts
                  .filter((p) => p.id !== selectedPost.id)
                  .slice(0, 5)
                  .map((p) => {
                    const img =
                      p._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "";
                    return (
                      <div
                        key={p.id}
                        className="see-more-card"
                        onClick={() => setSelectedPost(p)}
                      >
                        <img
                          src={img}
                          alt={p.title.rendered}
                          className="see-more-thumb"
                        />
                        <div
                          className="see-more-title-small"
                          dangerouslySetInnerHTML={{
                            __html: p.title.rendered,
                          }}
                        />
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
